import { ACTIONS, Action } from "./actions";
import { MODULES, Module } from "./modules";

/**
 * Permission String Type
 * Format: "module:action" (e.g., "employees:view")
 */
export type PermissionString = `${Module}:${Action}`;

/**
 * Helper function to create a permission string
 * @param module - Module name
 * @param action - Action name
 * @returns Permission string in format "module:action"
 */
export const createPermission = (
  module: Module,
  action: Action,
): PermissionString => {
  return `${module}:${action}`;
};

/**
 * Helper function to parse a permission string
 * @param permission - Permission string in format "module:action"
 * @returns Object with module and action, or null if invalid
 */
export const parsePermission = (
  permission: string,
): { module: Module; action: Action } | null => {
  const [module, action] = permission.split(":");
  if (
    !module ||
    !action ||
    !Object.values(MODULES).includes(module as Module) ||
    !Object.values(ACTIONS).includes(action as Action)
  ) {
    return null;
  }
  return {
    module: module as Module,
    action: action as Action,
  };
};

/**
 * Common permission strings for easy access
 */
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: createPermission(MODULES.DASHBOARD, ACTIONS.VIEW),

  // Integrations
  INTEGRATIONS_VIEW: createPermission(MODULES.INTEGRATIONS, ACTIONS.VIEW),

  // Social Assistance
  SOCIAL_ASSISTANCE_VIEW: createPermission(
    MODULES.SOCIAL_ASSISTANCE,
    ACTIONS.VIEW,
  ),
  SOCIAL_ASSISTANCE_EDIT: createPermission(
    MODULES.SOCIAL_ASSISTANCE,
    ACTIONS.EDIT,
  ),
  SOCIAL_ASSISTANCE_ADD: createPermission(
    MODULES.SOCIAL_ASSISTANCE,
    ACTIONS.ADD,
  ),
  SOCIAL_ASSISTANCE_DELETE: createPermission(
    MODULES.SOCIAL_ASSISTANCE,
    ACTIONS.DELETE,
  ),

  // Employees
  EMPLOYEES_VIEW: createPermission(MODULES.EMPLOYEES, ACTIONS.VIEW),
  EMPLOYEES_EDIT: createPermission(MODULES.EMPLOYEES, ACTIONS.EDIT),
  EMPLOYEES_ADD: createPermission(MODULES.EMPLOYEES, ACTIONS.ADD),
  EMPLOYEES_DELETE: createPermission(MODULES.EMPLOYEES, ACTIONS.DELETE),

  // Events
  EVENTS_VIEW: createPermission(MODULES.EVENTS, ACTIONS.VIEW),
  EVENTS_EDIT: createPermission(MODULES.EVENTS, ACTIONS.EDIT),
  EVENTS_ADD: createPermission(MODULES.EVENTS, ACTIONS.ADD),
  EVENTS_DELETE: createPermission(MODULES.EVENTS, ACTIONS.DELETE),

  // Youth Index
  YOUTH_INDEX_VIEW: createPermission(MODULES.YOUTH_INDEX, ACTIONS.VIEW),

  // Youth List
  YOUTH_LIST_VIEW: createPermission(MODULES.YOUTH_LIST, ACTIONS.VIEW),
  YOUTH_LIST_VIEW_PERSONAL_DATA: createPermission(
    MODULES.YOUTH_LIST,
    ACTIONS.VIEW_PERSONAL_DATA,
  ),

  // Youth Balance
  YOUTH_BALANCE_VIEW: createPermission(MODULES.YOUTH_BALANCE, ACTIONS.VIEW),
} as const;
